import React from "react";
import Container from "@mui/material/Container";
import { useNavigate } from "react-router-dom";

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  const sections = [
    {
      id: "introduction",
      title: "Introduction",
      content:
        "Koin ('we', 'our', or 'us') is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (the 'Platform') to participate in brand campaigns.",
    },
    {
      id: "information-collection",
      title: "Information We Collect",
      content:
        "We collect information in the following ways:",
      details: [
        "Personal Information: Name, phone number, email address, UPI ID, bank account details",
        "Device Information: Device type, operating system, unique device identifiers",
        "Campaign Activity: Campaign participation, actions taken, time spent, campaign completion data",
        "Verification Data: Submitted proof including images and videos for campaign validation",
        "Transaction Data: Payment history, withdrawal requests, incentive amounts",
      ],
    },
    {
      id: "use-of-information",
      title: "Use of Your Information",
      content:
        "We use the information we collect for the following purposes:",
      details: [
        "Enabling campaign participation and tracking user actions",
        "Processing incentive payouts to registered UPI IDs",
        "Verifying campaign completion and validating submitted proof",
        "Fraud detection and prevention to protect platform integrity",
        "Improving platform features and user experience",
        "Communicating campaign updates, support messages, and account notifications",
        "Analytics and research to enhance our services",
        "Complying with legal obligations and regulatory requirements",
        "Resolving disputes and enforcing platform terms",
      ],
    },
    {
      id: "data-sharing",
      title: "Data Sharing and Disclosure",
      content:
        "We do not sell, trade, or rent your personal information to third parties. We may share your information in the following limited circumstances:",
      details: [
        "Service Providers: With payment processors and UPI service providers to complete transactions",
        "Legal Requirements: When required by law or to protect our rights",
        "Business Transfers: In case of merger, acquisition, or sale of assets",
        "With Your Consent: When you explicitly authorize us to share information",
      ],
    },
    {
      id: "data-security",
      title: "Data Security",
      content:
        "We implement industry-standard security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.",
    },
    {
      id: "data-retention",
      title: "Data Retention",
      content:
        "We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy. You can request deletion of your account and associated data at any time by contacting us.",
    },
    {
      id: "cookies",
      title: "Cookies and Tracking Technologies",
      content:
        "We use cookies and similar tracking technologies to track activity on the App and hold certain information. These help us remember your preferences and understand user behavior. You can instruct your device to refuse cookies, though this may limit some functionality.",
    },
    {
      id: "third-party-links",
      title: "Third-Party Links",
      content:
        "The App may contain links to third-party websites and services. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before sharing any personal information.",
    },
    {
      id: "children-privacy",
      title: "Children's Privacy",
      content:
        "The App is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal information, we will take steps to delete such information and terminate the child's account.",
    },
    {
      id: "user-rights",
      title: "Your Rights",
      content:
        "You have the right to:",
      details: [
        "Access your personal information and request copies",
        "Correct inaccurate or incomplete information",
        "Request deletion of your account and data",
        "Opt-out of marketing communications",
        "Request a list of all data we hold about you",
      ],
    },
    {
      id: "policy-changes",
      title: "Changes to This Privacy Policy",
      content:
        "We may update this Privacy Policy from time to time. We will notify you of any significant changes by updating the date at the top of this page or by sending you a notification. Your continued use of the App following the posting of revised Privacy Policy means that you accept and agree to the changes.",
    },
    {
      id: "contact",
      title: "Contact Us",
      content:
        "If you have questions about this Privacy Policy or our privacy practices, please contact us at support@koinapp.co. We will respond to your inquiry within 24 hours.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Container
        maxWidth="md"
        sx={{
          pt: 3,
          pb: 4,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 16L6 10L12 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
          <div className="w-20" />
        </div>

        {/* Content */}
        <div className="space-y-6">
          {sections.map((section) => (
            <div key={section.id} className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <h2 className="text-lg font-bold text-gray-900 mb-3">
                {section.title}
              </h2>
              <p className="text-gray-700 leading-relaxed text-sm mb-3">
                {section.content}
              </p>
              {section.details && (
                <ul className="space-y-2 ml-4">
                  {section.details.map((detail, index) => (
                    <li
                      key={index}
                      className="text-gray-700 text-sm flex items-start gap-2"
                    >
                      <span className="text-orange-600 font-bold mt-0.5">•</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-8 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200 text-center">
          <p className="text-gray-700 text-sm mb-3">
            Questions about your privacy?
          </p>
          <a
            href="mailto:support@koinapp.co"
            className="inline-block px-6 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors"
          >
            Contact Us
          </a>
        </div>
      </Container>
    </div>
  );
}
